import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";

import type { SerializableAuthError } from "@/constants/Auth0Errors";
import { useAppDispatch, useAppSelector } from "@/store/hooks";

interface Auth0RenewFailurePayload {
  error: SerializableAuthError;
  extra?: Record<string, unknown>;
}

type AppStartupSliceState = {
  // Set when Auth0 credential renew fails; carries error context for logging / boundary flow.
  auth0RenewFailure?: Auth0RenewFailurePayload;
  // on web, app/(app)/_layout.tsc gates on postLoginRouting completion as a direct URL navigation otherwise races on that gate opening/postLoginRouting running
  // which results in the direct URL rendering briefly, then getting wiped by the replace to /home inexplicably
  canLoggedInUserSeeAppContent: boolean;
  isRootNavigatorReady: boolean;
};

const initialState: AppStartupSliceState = {
  auth0RenewFailure: undefined,
  canLoggedInUserSeeAppContent: false,
  isRootNavigatorReady: false,
};

const appStartupSlice = createSlice({
  // NOTE: extraReducers is explicitly omitted for the appStartupSlice, as extraReducers is used to reset the entire redux store on logout
  // however, we do not want to reset the appStartup redux store, as it indicates whether the app has been fully initialized.
  // when the app is closed/re-opened, the entire redux store will restart from scratch, and this slice will be reset properly then
  initialState,
  name: "appStartup",
  reducers: {
    // eslint-disable-next-line max-params
    setAuth0RenewFailure: (
      state,
      action: PayloadAction<Auth0RenewFailurePayload | undefined>,
    ) => {
      return {
        ...state,
        auth0RenewFailure: action.payload,
      };
    },
    // eslint-disable-next-line max-params
    setCanLoggedInUserSeeAppContent: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      return {
        ...state,
        canLoggedInUserSeeAppContent: action.payload,
      };
    },
    // eslint-disable-next-line max-params
    setIsRootNavigatorReady: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        isRootNavigatorReady: action.payload,
      };
    },
  },
});

const {
  setAuth0RenewFailure: setAuth0RenewFailureAction,
  setCanLoggedInUserSeeAppContent: setCanLoggedInUserSeeAppContentAction,
  setIsRootNavigatorReady: setIsRootNavigatorReadyAction,
} = appStartupSlice.actions;

const useIsRootNavigatorReadySelector = () => {
  return useAppSelector((state) => state.appStartup.isRootNavigatorReady);
};

const useCanLoggedInUserSeeAppContentSelector = () => {
  return useAppSelector(
    (state) => state.appStartup.canLoggedInUserSeeAppContent,
  );
};

const useAuth0RenewFailureSelector = () => {
  return useAppSelector((state) => state.appStartup.auth0RenewFailure);
};

const useAppStartupDispatch = () => {
  const dispatch = useAppDispatch();

  const setIsRootNavigatorReady = (isRootNavigatorReady: boolean) => {
    dispatch(setIsRootNavigatorReadyAction(isRootNavigatorReady));
  };

  const setCanLoggedInUserSeeAppContent = (
    canLoggedInUserSeeAppContent: boolean,
  ) => {
    dispatch(
      setCanLoggedInUserSeeAppContentAction(canLoggedInUserSeeAppContent),
    );
  };

  const setAuth0RenewFailure = (
    payload: Auth0RenewFailurePayload | undefined,
  ) => {
    dispatch(setAuth0RenewFailureAction(payload));
  };

  return {
    setAuth0RenewFailure,
    setCanLoggedInUserSeeAppContent,
    setIsRootNavigatorReady,
  };
};

export {
  setAuth0RenewFailureAction,
  setCanLoggedInUserSeeAppContentAction,
  setIsRootNavigatorReadyAction,
  useAppStartupDispatch,
  useAuth0RenewFailureSelector,
  useCanLoggedInUserSeeAppContentSelector,
  useIsRootNavigatorReadySelector,
};
export type { AppStartupSliceState, Auth0RenewFailurePayload };
export default appStartupSlice.reducer;
